import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import type { CamposDeLaFila } from "@/lib/informes";

import {
  GrupoDeIdentificacionDeInstalacion,
  GrupoDeValorizacionDeInstalacion,
  TotalesEstimados,
} from "./CamposDeInstalacion";
import { RotuloDeGrupo } from "./CamposDeConstruccion";
import { FotosDeLaFila } from "./FotosDeLaFila";
import {
  camposDesdeInstalacion,
  INSTALACION_VACIA,
  type FormularioDeInstalacion,
} from "./instalaciones";

interface FormularioDeNuevaInstalacionProps {
  ocupada: boolean;
  /** Con qué arranca: lo que la App ya sabe de una instalación creada en terreno. */
  inicial?: FormularioDeInstalacion;
  /** Crea la fila y, con el id que devuelve, sube las fotos retenidas. */
  onAgregar: (campos: CamposDeLaFila, fotos: File[]) => void;
  onCerrar: () => void;
}

/**
 * El formulario EN LÍNEA de la lista: agrega una instalación u obra nueva.
 *
 * SOLO EXIGE EL TIPO, al revés que construcciones —que pide además la
 * superficie—: el request no pide más, y la cantidad de una obra no siempre
 * está medida al cargarla. Sin ella la fila queda "Sin valorizar", que es lo
 * que la lista existe para mostrar.
 *
 * SIEMPRE AGREGA, nunca corrige: corregir una fila abre el modal, con su
 * propio estado, así que un borrador a medio escribir acá no se pierde al
 * abrir otra fila. Se desmonta al cerrar, y con él se van el borrador y las
 * fotos retenidas — "Cancelar" promete eso.
 *
 * LAS FOTOS SE RETIENEN en el navegador y viajan con el id que devuelve el
 * alta (`onAgregar`): hasta ahí no hay fila a la cual colgarlas.
 */
export function FormularioDeNuevaInstalacion({
  ocupada,
  inicial,
  onAgregar,
  onCerrar,
}: FormularioDeNuevaInstalacionProps) {
  const [formulario, setFormulario] = useState<FormularioDeInstalacion>(inicial ?? INSTALACION_VACIA);
  const [fotos, setFotos] = useState<File[]>([]);
  const [rechazoDeFoto, setRechazoDeFoto] = useState<string | null>(null);

  const puedeAgregar = formulario.tipo.trim() !== "";

  function escribir(cambio: Partial<FormularioDeInstalacion>) {
    setFormulario((actual) => ({ ...actual, ...cambio }));
  }

  function agregar() {
    if (!puedeAgregar) return;

    onAgregar(camposDesdeInstalacion(formulario), fotos);
    onCerrar();
  }

  const comunes = { idPrefijo: "instalacion", formulario, ocupada, escribir };

  return (
    <div
      id="nueva-instalacion"
      className="bg-superficie-sutil border-divisor flex scroll-mt-5 flex-col gap-3.5 border-y-[1.5px] px-5 pt-4 pb-4.5"
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <h4 className="text-ink text-[0.84375rem] font-bold">Nueva instalación u obra</h4>
        <span className="text-ink-muted text-caption">
          {inicial
            ? "Viene de terreno con lo que el encargado anotó. Completa la valorización con sus fotos y audios."
            : "Completa al menos el tipo; la valorización puede quedar para después."}
        </span>
      </div>

      <RotuloDeGrupo>Identificación</RotuloDeGrupo>
      <GrupoDeIdentificacionDeInstalacion {...comunes} requeridos={["tipo"]} />

      <RotuloDeGrupo aparte="Opcional al crear">Valorización</RotuloDeGrupo>
      <GrupoDeValorizacionDeInstalacion {...comunes} />

      <TotalesEstimados formulario={formulario} />

      <FotosDeLaFila
        titulo="Fotos"
        etiquetaDelSelector="Adjuntar fotos de la instalación"
        ocupada={ocupada}
        editando={false}
        pendientes={fotos}
        // Nunca corrige una fila que ya existe: `editando={false}` hace que se
        // dibuje la lista de PENDIENTES y no la de guardadas.
        guardadas={[]}
        urlDe={() => ""}
        alt="Foto de la instalación"
        error={rechazoDeFoto}
        onElegidas={(elegidos) => setFotos((actuales) => [...actuales, ...elegidos])}
        onRechazo={setRechazoDeFoto}
        onQuitarPendiente={(indice) => setFotos((actuales) => actuales.filter((_, i) => i !== indice))}
        onQuitarGuardada={() => {}}
      />

      <div className="flex flex-wrap items-center gap-3 pt-1">
        <Button type="button" onClick={agregar} disabled={ocupada || !puedeAgregar}>
          {ocupada ? <Spinner /> : null}
          Agregar a la lista
        </Button>
        <Button type="button" variant="ghost" onClick={onCerrar}>
          Cancelar
        </Button>
        {puedeAgregar ? null : <span className="text-ink-hint text-caption">Falta: tipo</span>}
      </div>
    </div>
  );
}
